import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Check, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientPlan = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const plans = [
    { id: 'starter', name: 'Starter', price: '£0 first month, then £25/mo', features: ['Managed website', 'Client portal', '1 update/month', 'Basic maintenance', 'Backups'] },
    { id: 'professional', name: 'Professional', price: '£49/mo', features: ['All Starter features', '3 updates/month', 'Priority support', 'Uptime monitoring', 'Monthly reports'] },
    { id: 'business', name: 'Business', price: '£89/mo', features: ['All Professional features', 'Unlimited updates', 'Enhanced security', 'Azure integration', 'Priority features'] }
  ];

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/client/request-plan-change`, {
        requested_plan_id: selectedPlan,
        note: note || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan change request submitted!');
      setOpen(false);
      setSelectedPlan('');
      setNote('');
    } catch (error) {
      console.error('Failed to submit plan change:', error);
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="client-plan-page">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="plan-title">Your Plan</h1>
            <p className="text-slate-400">Manage your subscription and request changes</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-slate-200" data-testid="request-change-btn">
                Request Plan Change
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950 border-slate-800 text-white">
              <DialogHeader>
                <DialogTitle>Request Plan Change</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Select a new plan and provide any additional notes
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">New Plan</label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="plan-select">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-white">
                      {plans.filter(p => p.id !== dashboard?.plan_id).map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>{plan.name} - {plan.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Notes (Optional)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Any specific requirements..."
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="plan-change-note"
                  />
                </div>
                <Button
                  onClick={handlePlanChange}
                  disabled={loading}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="submit-plan-change"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const isCurrentPlan = dashboard?.plan_id === plan.id;
            return (
              <div
                key={plan.id}
                className={`rounded-2xl p-8 transition-all ${
                  isCurrentPlan
                    ? 'bg-gradient-to-b from-indigo-500/20 to-purple-500/20 border-2 border-indigo-500/50 glow'
                    : 'bg-slate-900/50 border border-white/10'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isCurrentPlan && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-indigo-500 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid="current-plan-badge">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold font-['Syne'] text-white mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                </div>
                
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Check className="text-indigo-400 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ClientPlan;
